import { ApiError } from '~libs/axiosWrapper';

import { Budget } from '~support-at-home/models';

type BulkUpdateConflict = {
  group: string;
  conflicting_budget_ids: Array<string | number>;
};

/**
 * Cross-references a list of budget IDs (flagged by the API on a bulk-update rejection) against the
 * loaded budgets to build human-readable, deduplicated labels. Falls back to the raw ID when a
 * flagged budget is not present in the loaded list. Shared by both bulk-update banners (conflict and
 * needs-review) so the label format stays consistent as more banner types are added.
 *
 * @param ids - The budget IDs the API flagged
 * @param budgets - The currently loaded budgets (each carries reference and client name)
 * @returns Deduplicated labels like "BD-031256 — Maria Bugeja"
 */
const labelBudgetIds = (ids: Array<string | number>, budgets: Budget[]): string[] => {
  const budgetsById = new Map(budgets.map((budget) => [String(budget.id), budget]));

  const labels = ids.map((id) => {
    const budget = budgetsById.get(String(id));
    if (!budget) {
      return String(id);
    }
    // Only append the client name when present, so a missing name never leaves a dangling separator.
    return budget.client?.full_name ? `${budget.reference} - ${budget.client.full_name}` : budget.reference;
  });

  return [...new Set(labels)];
};

/**
 * Builds human-readable labels for the budgets the API flagged as conflicting during a bulk
 * status update. The API returns the conflicting budget IDs in `error.data.details`; we cross-
 * reference those against the loaded budgets to surface the reference and client name so users
 * can identify and resolve the conflict (TAV-2410).
 *
 * @param error - The RTK Query error from the bulk update mutation
 * @param budgets - The currently loaded budgets (each carries reference and client name)
 * @returns Deduplicated labels like "BD-031256 — Maria Bugeja"; empty when no conflict details
 */
export const getBulkUpdateConflictLabels = (error: unknown, budgets: Budget[]): string[] => {
  const details = (error as ApiError | undefined)?.data as { details?: BulkUpdateConflict[] } | undefined;
  if (!details || !Array.isArray(details.details)) {
    return [];
  }

  const ids = details.details.flatMap((conflict) => conflict.conflicting_budget_ids ?? []);
  return labelBudgetIds(ids, budgets);
};

/**
 * Builds human-readable labels for the budgets the API flagged as needing review during a bulk
 * status update. The API returns the flagged budget IDs in `error.data.needs_review_ids`; we cross-
 * reference those against the loaded budgets to surface the reference and client name so users can
 * identify and resolve them (TAV-2970).
 *
 * @param error - The RTK Query error from the bulk update mutation
 * @param budgets - The currently loaded budgets (each carries reference and client name)
 * @returns Deduplicated labels like "BD-031256 — Maria Bugeja"; empty when no needs_review_ids
 */
export const getBulkUpdateNeedsReviewLabels = (error: unknown, budgets: Budget[]): string[] => {
  const data = (error as ApiError | undefined)?.data as { needs_review_ids?: Array<string | number> } | undefined;
  if (!data || !Array.isArray(data.needs_review_ids)) {
    return [];
  }

  return labelBudgetIds(data.needs_review_ids, budgets);
};
